import { clsx } from 'clsx';
import React from 'react';
import { Checkbox } from './checkbox';

type BulkActionBarProps = {
  count: number;
  busy?: boolean;
  onClear: () => void;
  children: React.ReactNode;
  /**
   * A smaller bar with "N selected" centered on its own row above the actions — for a
   * bar that floats over a narrow drawer tab (the document selection bar).
   */
  compact?: boolean;
  'data-testid'?: string;
};

/** Whether the buttons sit in a compact bar (they shrink with it). */
const CompactBarContext = React.createContext(false);

/**
 * Floating pill pinned to the bottom of a scrolling list while rows are
 * selected (pair with `useRowSelection`). Actions go in as `BulkActionButton`
 * children. On a narrow pill the actions wrap and every line is centered; the
 * clear ✕ stays at the right end of the first line whatever wraps. Renders
 * nothing when nothing is selected.
 */
export const BulkActionBar: React.FC<BulkActionBarProps> = ({
  count,
  busy = false,
  onClear,
  children,
  compact = false,
  'data-testid': testId = 'bulk-action-bar',
}) => {
  if (count === 0) return null;
  return (
    <div className="tw:pointer-events-none tw:sticky tw:bottom-4 tw:z-20 tw:mt-3 tw:flex tw:justify-center">
      <div
        role="toolbar"
        aria-label="Bulk actions"
        aria-busy={busy}
        data-testid={testId}
        className={clsx(
          'tw:pointer-events-auto tw:flex tw:bg-brand-green-0 tw:text-white tw:shadow-lg',
          compact
            ? // the count row is centered over the actions, so the ✕ floats in the corner
              'tw:relative tw:rounded-xl tw:px-2 tw:pt-1 tw:pb-1.5'
            : 'tw:items-start tw:gap-2 tw:rounded-2xl tw:py-2 tw:pr-2 tw:pl-3'
        )}
      >
        <CompactBarContext.Provider value={compact}>
          {compact ? (
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-1">
              <span className="tw:px-6 tw:text-xs tw:tabular-nums" data-testid={`${testId}-count`}>
                {count} selected
              </span>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-1">
                {children}
              </div>
            </div>
          ) : (
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-center tw:gap-2">
              <span className="tw:text-sm tw:tabular-nums">{count} selected</span>
              {children}
            </div>
          )}
        </CompactBarContext.Provider>
        <button
          type="button"
          aria-label="Clear selection"
          onClick={onClear}
          disabled={busy}
          className={clsx(
            'tw:grid tw:shrink-0 tw:place-items-center tw:!rounded-full tw:hover:bg-white/15 tw:disabled:opacity-50',
            compact ? 'tw:absolute tw:top-0.5 tw:right-0.5 tw:size-5 tw:text-xs' : 'tw:size-7'
          )}
        >
          <i className="far fa-times" />
        </button>
      </div>
    </div>
  );
};

type BulkActionButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  className?: string;
};

export function BulkActionButton({ className, ...props }: BulkActionButtonProps) {
  const compact = React.useContext(CompactBarContext);
  return (
    <button
      type="button"
      {...props}
      className={clsx(
        // !rounded-lg: Bootstrap's unlayered reboot (`button { border-radius: 0 }`) beats
        // the plain utility and leaves the pills square
        'tw:bg-white/15 tw:font-medium tw:hover:bg-white/25 tw:disabled:cursor-not-allowed tw:disabled:opacity-50',
        compact
          ? // !text-xs: the reboot's `button { font-size: inherit }` beats the plain utility too
            'tw:!rounded-md tw:px-1.5 tw:py-px tw:!text-[11px]/4'
          : 'tw:!rounded-lg tw:px-3 tw:py-1 tw:text-sm',
        className
      )}
    />
  );
}

type RowSelectCheckboxProps = {
  checked: boolean;
  onChange: () => void;
  label: string;
  indeterminate?: boolean;
  disabled?: boolean;
  title?: string;
  className?: string;
  'data-testid'?: string;
};

/**
 * Row/card selection checkbox for lists whose rows also open a drawer: swallows
 * the click and key events so selecting never opens the row.
 */
export function RowSelectCheckbox({
  checked,
  onChange,
  label,
  indeterminate = false,
  disabled = false,
  title,
  className,
  'data-testid': testId,
}: RowSelectCheckboxProps) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <span
      className={clsx('tw:inline-flex', className)}
      title={title}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <Checkbox
        color="brandgreen"
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
        onChange={onChange}
        aria-label={label}
        data-testid={testId}
      />
    </span>
  );
}
